class TestimonialsSlider {
  constructor(root) {
    this.root = root;
    this.currentSlide = 0;
    this.sliderWrapper = this.root.querySelector('.slider-wrapper');
    this.prevBtn = this.root.querySelector('.nav-button.prev');
    this.nextBtn = this.root.querySelector('.nav-button.next');
    this.indicators = this.root.querySelectorAll('.indicator');
    this.slides = this.sliderWrapper ? this.sliderWrapper.querySelectorAll('.slide') : [];
    this.gapPx = 0;
    this.slideStepPx = 0; // distance to move per slide (slide width + gap)
    this.isAnimating = false;
    
    // Calculate total slides based on indicators
    this.totalSlides = this.indicators.length;

    if (this.sliderWrapper && this.totalSlides > 0) {
      this.init();
    }
  }

  init() {
    // Measure slide width and gap
    this.calculateMetrics();

    // Add event listeners for navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }

    // Add touch/swipe support
    this.addTouchSupport();

    // Add indicator click support
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => this.goToSlide(index));
    });

    // Auto-play functionality (optional - commented out by default)
    // this.startAutoPlay();

    // Initialize first slide
    this.applyTransform();
    this.updateIndicators();

    // Reveal slider after first paint to avoid initial background flash
    const sliderEl = this.root.querySelector('.testimonials-slider');
    if (sliderEl) {
      const reveal = () => {
        // Force a reflow then add the ready class
        void sliderEl.offsetHeight;
        sliderEl.classList.add('is-ready');
      };
      if (window.requestAnimationFrame) {
        requestAnimationFrame(() => reveal());
      } else {
        setTimeout(reveal, 0);
      }
    }

    // Recompute metrics on resize
    this._onResize = () => {
      this.calculateMetrics();
      this.applyTransform();
    };
    window.addEventListener('resize', this._onResize);
  }

  calculateMetrics() {
    if (!this.sliderWrapper) return;
    const styles = window.getComputedStyle(this.sliderWrapper);
    // Flex gap can be reported as columnGap for row-direction flex
    const gap = parseFloat(styles.columnGap) || parseFloat(styles.gap) || 0;
    this.gapPx = isNaN(gap) ? 0 : gap;
    // Determine slide width from the first slide
    if (this.slides && this.slides.length) {
      const firstSlideRect = this.slides[0].getBoundingClientRect();
      this.slideStepPx = firstSlideRect.width + this.gapPx;
    } else {
      // Fallback to the visible container width
      const container = this.sliderWrapper.parentElement;
      this.slideStepPx = (container ? container.getBoundingClientRect().width : 0) + this.gapPx;
    }
  }

  applyTransform() {
    if (!this.sliderWrapper) return;
    const translateX = -(this.currentSlide * this.slideStepPx);
    // Preserve GPU compositing by keeping translateZ(0)
    this.sliderWrapper.style.transform = `translateX(${translateX}px) translateZ(0)`;
  }

  addTouchSupport() {
    let startX = 0;
    let currentX = 0;
    let diffX = 0;

    this.sliderWrapper.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    this.sliderWrapper.addEventListener('touchmove', (e) => {
      if (!startX) return;
      currentX = e.touches[0].clientX;
      diffX = startX - currentX;
    }, { passive: true });

    this.sliderWrapper.addEventListener('touchend', () => {
      if (Math.abs(diffX) > 50) {
        if (diffX > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }
      startX = 0;
      currentX = 0;
      diffX = 0;
    }, { passive: true });
  }

  goToSlide(slideIndex) {
    if (this.isAnimating || slideIndex === this.currentSlide || slideIndex >= this.totalSlides) return;

    this.isAnimating = true;
    this.currentSlide = slideIndex;
    
    this.applyTransform();

    this.updateIndicators();

    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }

  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.totalSlides;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = this.currentSlide === 0 ? this.totalSlides - 1 : this.currentSlide - 1;
    this.goToSlide(prevIndex);
  }

  updateIndicators() {
    this.indicators.forEach((indicator, index) => {
      if (index === this.currentSlide) {
        indicator.classList.add('active');
      } else {
        indicator.classList.remove('active');
      }
    });
  }

  startAutoPlay() {
    // Optional: Auto-advance slides every 5 seconds
    setInterval(() => {
      this.nextSlide();
    }, 5000);
  }
}

// Video play button functionality
function initVideoPlayButtons() {
  document.querySelectorAll('.play-button').forEach(button => {
    button.addEventListener('click', function(e) {
      e.preventDefault();
      
      // Visual feedback
      this.style.transform = 'scale(0.95)';
      setTimeout(() => {
        this.style.transform = 'scale(1)';
      }, 150);
      
      // Get video URL from data attribute
      const videoUrl = this.getAttribute('data-video-url');
      
      if (videoUrl) {
        // Open video in new tab/window or implement modal
        window.open(videoUrl, '_blank');
      } else {
        // Placeholder action - could trigger a modal or other video player
        console.log('Play video clicked - implement video player logic here');
      }
    });
  });
}

// Initialize everything when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Initialize all instances on the page
  document.querySelectorAll('.testimonials-section').forEach((sectionEl) => {
    if (sectionEl.querySelector('.testimonials-slider')) {
      new TestimonialsSlider(sectionEl);
    }
  });
  // Initialize video play buttons
  initVideoPlayButtons();
});

// Optional: Re-init on Shopify Theme Editor section load
document.addEventListener('shopify:section:load', (event) => {
  const sectionId = event.detail && event.detail.sectionId;
  const mount = sectionId ? document.getElementById('shopify-section-' + sectionId) : null;
  const root = mount ? mount.querySelector('.testimonials-section') : null;
  if (root && root.querySelector('.testimonials-slider')) {
    new TestimonialsSlider(root);
    initVideoPlayButtons();
  }
});
